// Pure DAG-shaping for the "Custom plans" breakdown's DAG rail (#4341) — turns
// a plan's `PlanProjectionGroup[]` (already loaded by `usePlan`, `lib/plan.ts`)
// into the ordered, humanised nodes the rail renders. No I/O; the projection
// itself is the fetch.

import type {
  PlanEdgeRelease,
  PlanLegDeploy,
  PlanLegMergeHold,
  PlanProjectionEdge,
  PlanProjectionGroup,
} from '@developerz/domain';
import { PlanEdgeHold, planLegLive, TaskHoldReason } from '@developerz/domain';

/**
 * One DAG rail node. `hold` is null for a group with no `heldOn` — running,
 * terminal, or merely queued behind sibling work with no gate naming a reason.
 * `hold.labelKey` is always `fleet.plans.custom.held.<reason>`: EVERY member
 * of {@link TaskHoldReason} maps to a labelKey of that shape
 * (`lib/plan-dag.test.ts` proves the shape, iterating every member and
 * cross-checking the list against {@link TaskHoldReasonSchema} directly), and
 * a translation exists in `locales/en.json` for every one of them
 * (`i18n.test.ts`, "custom-plans labels cover every disposition, stall
 * reason, merge decision and held reason" — iterating
 * {@link TaskHoldReasonSchema} too, never a hand-kept list, so a member added
 * to the domain without catalog copy fails THAT seam instead of rendering
 * `⟦fleet.plans.custom.held.<reason>⟧` on the DAG rail).
 */
export type PlanDagNode = {
  groupId: string;
  position: number;
  title: string;
  disposition: PlanProjectionGroup['disposition'];
  dependsOn: readonly string[];
  hold: { labelKey: string; upstreamTitle: string | null } | null;
  /** Every edge INTO this node (plan 101 slice 12), upstream resolved to its title. */
  edges: readonly PlanDagEdge[];
};

/** One edge into a rail node: how it releases, what it holds, and whether it has released. */
export type PlanDagEdge = {
  upstreamTitle: string;
  release: PlanEdgeRelease;
  environment: string | null;
  hold: PlanEdgeHold;
  released: boolean;
};

/**
 * The slice-12 leg facts of one group, read through ONE seam. The three arrays are required
 * on the wire, but `usePlan` reads with the cast overload of `apiFetch` and parses nothing
 * (`PlanCard.tsx` spells out that roll window), so a group served by an API pod that predates
 * slice 12 arrives without them. Absent reads as none: no edge, no deploy, no merge hold —
 * what that pod's plan honestly had to say. Every reader below goes through here, so the
 * window is handled once and retires with this function.
 */
export function legWire(group: PlanProjectionGroup): {
  edges: readonly PlanProjectionEdge[];
  deploy: readonly PlanLegDeploy[];
  mergeHeld: readonly PlanLegMergeHold[];
} {
  return {
    edges: group.edges ?? [],
    deploy: group.deploy ?? [],
    mergeHeld: group.mergeHeld ?? [],
  };
}

/**
 * What a leg is WAITING on, by upstream title: an unreleased `hold: start` edge holds the
 * leg's start, and every `mergeHeld` entry holds its merge (`mergeHeldOf` on the server, the
 * same reader the merge egress refuses with). A released edge is not a wait — it renders as
 * a plain dependency, so every edge into a leg says exactly one thing.
 */
export type LegWait = {
  readonly upstreamTitle: string;
  readonly release: PlanEdgeRelease;
  readonly environment: string | null;
  readonly at: PlanEdgeHold;
};

export function legWaits(
  group: PlanProjectionGroup,
  byId: ReadonlyMap<string, Pick<PlanProjectionGroup, 'title'>>,
): LegWait[] {
  const wire = legWire(group);
  const title = (id: string): string => byId.get(id)?.title ?? id;
  const atStart = wire.edges
    .filter((e) => e.hold === PlanEdgeHold.Start && !e.released)
    .map((e) => ({
      upstreamTitle: title(e.upstream),
      release: e.release,
      environment: e.environment,
      at: PlanEdgeHold.Start,
    }));
  const atMerge = wire.mergeHeld.map((m) => ({
    upstreamTitle: title(m.upstreamGroupId),
    release: m.release,
    environment: m.environment,
    at: PlanEdgeHold.Merge,
  }));
  return [...atStart, ...atMerge];
}

/** The upstream ids whose edge into `group` has RELEASED — the plain "Depends on" facts. */
export function releasedUpstreams(group: PlanProjectionGroup): string[] {
  return legWire(group)
    .edges.filter((e) => e.released)
    .map((e) => e.upstream);
}

/**
 * Legs grouped by repo, in plan order: the repos by the position of their first leg (the
 * lead's repo first), each repo's legs in `(position, groupId)` order. A goal spans repos,
 * and "what is happening in acme/product" is the question a reader of one asks first.
 */
export function legsByRepo<
  T extends Pick<PlanProjectionGroup, 'position' | 'groupId' | 'repoFullName'>,
>(groups: readonly T[]): { repo: string; legs: T[] }[] {
  const byRepo = new Map<string, T[]>();
  for (const group of orderedGroups(groups)) {
    const legs = byRepo.get(group.repoFullName);
    if (legs === undefined) byRepo.set(group.repoFullName, [group]);
    else legs.push(group);
  }
  return [...byRepo].map(([repo, legs]) => ({ repo, legs }));
}

/**
 * "N of M legs live": N is the domain's {@link planLegLive} over each leg, M every leg a
 * repair has not replaced (plan 101 D7): a `superseded` leg's replacement is counted instead.
 */
export function legsLive(groups: readonly PlanProjectionGroup[]): { live: number; total: number } {
  const legs = groups.filter((g) => g.disposition !== 'superseded');
  const live = legs.filter((g) =>
    planLegLive({ disposition: g.disposition, deploy: legWire(g).deploy }),
  ).length;
  return { live, total: legs.length };
}

/**
 * The upstream group whose merge would lift an `upstream_group_unmerged` hold.
 * A group may depend on several siblings; the one still short of `merged` is
 * the one actually holding this row (`upstreamGroupUnmergedSql`'s own
 * predicate), so that is the title surfaced. If every dependency already
 * reads `merged` — a narrow redelivery window, the read racing the group's own
 * flip — fall back to the first dependency named, rather than reporting no
 * upstream at all for a hold that is, by construction, non-null.
 */
function resolveUpstreamTitle(
  group: PlanProjectionGroup,
  byId: ReadonlyMap<string, PlanProjectionGroup>,
): string | null {
  const deps = group.dependsOn.map((id) => byId.get(id)).filter((dep) => dep !== undefined);
  const first = deps[0];
  if (first === undefined) return null;
  const stillUnmerged = deps.find((dep) => dep.status !== 'merged');
  return (stillUnmerged ?? first).title;
}

/**
 * Groups ordered `(position, groupId)` — `groupId` is the tiebreak because
 * `position` is NOT unique (two groups can share a position;
 * `PlanProjectionGroupSchema` only requires it non-negative). Exported so the
 * DAG rail (via {@link planDagNodes}) and `PlanGroupTable.tsx` sort by the
 * SAME comparator instance rather than each hand-copying the tiebreak — two
 * independent literals can silently desync; importing one function cannot.
 */
export function orderedGroups<T extends Pick<PlanProjectionGroup, 'position' | 'groupId'>>(
  groups: readonly T[],
): T[] {
  return [...groups].sort(
    (a, b) =>
      a.position - b.position || (a.groupId < b.groupId ? -1 : a.groupId > b.groupId ? 1 : 0),
  );
}

/**
 * Groups ordered `(position, groupId)` via {@link orderedGroups}, then
 * humanised into rail nodes.
 */
export function planDagNodes(groups: readonly PlanProjectionGroup[]): PlanDagNode[] {
  const byId = new Map(groups.map((g) => [g.groupId, g] as const));
  const ordered = orderedGroups(groups);
  return ordered.map((group) => ({
    groupId: group.groupId,
    position: group.position,
    title: group.title,
    disposition: group.disposition,
    dependsOn: group.dependsOn,
    edges: legWire(group).edges.map((e) => ({
      upstreamTitle: byId.get(e.upstream)?.title ?? e.upstream,
      release: e.release,
      environment: e.environment,
      hold: e.hold,
      released: e.released,
    })),
    hold:
      group.heldOn === null
        ? null
        : {
            labelKey: `fleet.plans.custom.held.${group.heldOn.reason}`,
            upstreamTitle:
              group.heldOn.reason === TaskHoldReason.UpstreamGroupUnmerged
                ? resolveUpstreamTitle(group, byId)
                : null,
          },
  }));
}
